import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { PermissionsConfig, CustomerGroups } from '@api/index';
import { AVAILABLE_PERMISSIONS } from '@app/constants';
import { PERMISSIONS_HEADER_CONFIG } from '@app/shared/permissions/permissions-header.config';

@Component({
  selector: 'flow-permissions-table',
  templateUrl: './permissions-table.component.html',
  styleUrls: ['./permissions-table.component.scss'],
})
export class PermissionsTableComponent implements OnInit {
  @Input() permissionList: PermissionsConfig = { nodes: [] };
  @Input() isEditable: boolean = true;
  @Input() selectablePermissions: string[] = [];
  @Input() headerTitle: string = '';
  @Input() actions: string[] = [];
  @Input() groups: CustomerGroups[] = [];
  @Output() actionClick: EventEmitter<{ action: string; node: any }> = new EventEmitter();

  selectAll: boolean;

  headerConfig = PERMISSIONS_HEADER_CONFIG.map(header => ({
    ...header,
    colSpan: header.actions.reduce((sum, action) => sum + action.permissions.length, 0),
  }));

  // Exclude the first element ("All") from AVAILABLE_PERMISSIONS
  // since it is not needed in the permissions table.
  availablePermissions = AVAILABLE_PERMISSIONS.slice(1);
  permissionsForm: FormGroup;

  constructor(private formBuilder: FormBuilder) {}

  ngOnInit(): void {
    this.reinitializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.permissionList && !changes.permissionList.firstChange) {
      this.reinitializeForm();
    }
    if (changes.isEditable && !changes.isEditable.firstChange) {
      this.reinitializeForm();
    }
  }

  reinitializeForm() {
    this.permissionsForm = this.formBuilder.group({
      nodes: this.formBuilder.array(this.permissionList.nodes.map(node => this.createNodeForm(node))),
    });
  }

  checkSelectedAll(permissionsFormArray: FormArray<FormGroup>) {
    return permissionsFormArray.controls.every(obj => obj?.value?.value === true);
  }

  createNodeForm(node): FormGroup {
    const permissionsArray = this.availablePermissions.map(permission => this.createPermissionControl(node.permissions, permission));
    const permissionsFormArray = this.formBuilder.array(permissionsArray);
    const allTrue = this.checkSelectedAll(permissionsFormArray);

    return this.formBuilder.group({
      id: node.id,
      name: node.name,
      data: node.data,
      selectAll: allTrue,
      permissions: permissionsFormArray,
    });
  }

  createPermissionControl(permissions, permission): FormGroup {
    const isPermissionAvailable = permissions.includes(permission);
    const isPermissionEnabled = this.selectablePermissions.includes(permission);
    return this.formBuilder.group({
      label: [permission],
      value: [{ value: isPermissionAvailable, disabled: this.isEditable ? !isPermissionEnabled : true }],
    });
  }

  get nodes(): FormArray {
    return this.permissionsForm.get('nodes') as FormArray;
  }

  get selectAllProp() {
    return this.permissionsForm.get('selectAll');
  }

  permissions(nodeIndex: number): FormArray {
    return this.nodes.at(nodeIndex).get('permissions') as FormArray;
  }

  isFormControl(control: any): control is FormControl {
    return control instanceof FormControl;
  }

  onNodeSelectAll(node: AbstractControl, event: MatCheckboxChange): void {
    const formGroup = node as FormGroup;
    const permissionsControl = formGroup.get('permissions') as FormArray;
    permissionsControl.controls.forEach(permission => {
      if (!permission.get('value').disabled) {
        permission.get('value').setValue(event.checked);
      }
    });
  }

  getFormValue(): any {
    return this.permissionsForm.value;
  }

  onGroupChange(customerId: number, roleId: number): void {
    const nodeIndex = this.nodes.controls.findIndex(n => (n as FormGroup).get('id')?.value === customerId);
    if (nodeIndex === -1) {
      return;
    }

    const originalNode = this.permissionList.nodes.find(n => n.id === customerId);
    if (originalNode) {
      this.nodes.setControl(nodeIndex, this.createNodeForm(originalNode));
    }

    if (roleId === null) {
      return;
    }

    const customerGroup = this.groups.find(g => g.customerId === customerId);
    const group = customerGroup?.roles.find(r => r.id === roleId);
    
    if (!group) {
      return;
    }

    const groupPermissions = (!group.permissionList || group.permissionList.trim() === '')
      ? []
      : group.permissionList.split(',').map(p => p.trim()).filter(Boolean);
    
    const permissionSet = new Set(groupPermissions);
    const node = this.nodes.at(nodeIndex) as FormGroup;
    const permissionsArray = node.get('permissions') as FormArray;

    permissionsArray.controls.forEach(control => {
      const label = control.get('label')?.value;
      const valueControl = control.get('value');
      if (!valueControl?.disabled) {
        valueControl.setValue(permissionSet.has(label));
      }
    });

    node.get('selectAll')?.setValue(this.checkSelectedAll(permissionsArray));
    node.disable();
  }

  getSelectedPermissions(): any {
    return this.permissionsForm.value.nodes.map(node => {
      const selectedPermissions = node.permissions.filter(permission => permission.value).map(permission => permission.label);

      return {
        id: node.id,
        permissions: selectedPermissions,
      };
    });
  }

  onActionClick(action: string, node: any) {
    this.actionClick.emit({ action, node });
  }

  getTranslatedActionKey(actionKey: string): string {
    return `permissionTableActions.${actionKey}`;
  }
}
